import { ElementRenderer } from '../renderer/elementRenderer';
import parseExternal from './parseExternal.js';
import { UserMessageNotice } from '../customNotice/clientNotice/userMessageNotice';
import { DebugInfoEvent } from '../events/botEvents/debugInfoEvent';
import { InvoiceEvent } from '../events/botEvents/invoiceEvent';
export default class ChatBotParser {
    constructor() {
        /**
         * @param userInput text from user/client
         * @returns a user message object in html representation
         */
        this.parseUserInput = (userInput) => {
            const elementRenderer = new ElementRenderer();
            elementRenderer.addUserInput(userInput);
            return elementRenderer.getResult();
        };
        /**
        * Get the type of the sender of the message
        *
        * @param message genesys message object
        *
        * @returns type of the sender of the message: 'agent', 'bot', 'system'
        */
        this.getMessageSenderType = (message) => {
            if (message.from && message.from.type && message.from.type == "Agent") {
                if (message.eventAttributes && message.eventAttributes.GCTI_SYSTEM
                    && message.eventAttributes.GCTI_SYSTEM['party-info']
                    && message.eventAttributes.GCTI_SYSTEM['party-info'].style == "BOT") {
                    return 'bot';
                }
                else {
                    return 'agent';
                }
            }
            else if (message.from && message.from.type && message.from.type == "Client") {
                return 'client';
            }
            else {
                return 'system';
            }
        };
    }
    /**
     * Parses a single message and adds it to the attached ElementRenderer
     *
     * @param message to parse
     * @param renderer the message should be added to
     * @param userMessageMapping mapping of user messages that need to be remapped
     * @param widget application used for elements to get access to api
     */
    parseWebChatServiceMessage(message, renderer, widget) {
        if (message) {
            try {
                let messageSenderType = this.getMessageSenderType(message);
                switch (message.from.type) {
                    case 'Client':
                        if ('CustomNotice' === message.type) {
                            try {
                                let customNotice = JSON.parse(message.text);
                                switch (customNotice.type) {
                                    case UserMessageNotice.getType():
                                        let userMessageNotice = customNotice;
                                        if (userMessageNotice.senderId !== widget.getWidgetApplicationId()) {
                                            renderer.setUserImage('client');
                                            renderer.addUserInput(userMessageNotice.userInput);
                                        }
                                        break;
                                }
                            }
                            catch (e) {
                                //Unknown custom notice
                            }
                        }
                        // ignore client messages with no mapping
                        break;
                    case 'System':
                        renderer.setUserImage(messageSenderType);
                        parseExternal(message, renderer);
                        break;
                    case 'External':
                        renderer.setUserImage(messageSenderType);
                        parseExternal(message, renderer);
                        break;
                    default:
                        if ('Message' === message.type) {
                            if (message['eventAttributes'] &&
                                message['eventAttributes']['structured-content'] &&
                                message['eventAttributes']['structured-content']['genesys-chat'] &&
                                message['eventAttributes']['structured-content']['genesys-chat']['content']) {
                                try {
                                    let json = JSON.parse(message['eventAttributes']['structured-content']['genesys-chat']['content']);
                                    let gdpr = json.hasOwnProperty('gdpr') && null !== json.gdpr ? json.gdpr : true;
                                    // ignore message if gdpr is not accepted
                                    if (gdpr) {
                                        renderer.setUserImage(messageSenderType);
                                        this.handleStructuredContentJSON(renderer, message, json);
                                    }
                                }
                                catch (e) {
                                    console.log("ERROR PARSING");
                                    console.log(e);
                                }
                            }
                            else {
                                // no structured content
                                renderer.setUserImage(messageSenderType);
                                renderer.addText(message);
                            }
                        }
                        else if ('FileUploaded' === message.type) {
                            renderer.setUserImage(messageSenderType);
                            renderer.addDownloadButton(message.userData, widget);
                        }
                        else if ('PushUrl' === message.type) {
                            renderer.setUserImage(messageSenderType);
                            renderer.addText(message);
                        }
                        break;
                }
            }
            catch (error) {
            }
        }
    }
    /**
     * Parses structured content of message object if available
     * @param message object
     * @returns JSON object of structured content or null if structured content not existant
     */
    parseStructuredContent(message) {
        if (message['eventAttributes']['structured-content'] &&
            message['eventAttributes']['structured-content']['genesys-chat'] &&
            message['eventAttributes']['structured-content']['genesys-chat']['content']) {
            return JSON.parse(message['eventAttributes']['structured-content']['genesys-chat']['content']);
        }
        return null;
    }
    /**
     * Function used determine which renderFunction to call
     * @param {*} renderer ElementRenderer that holds the final result
     * @param {*} structuredContent preparsed structuredContent JSON that holds content, contentType, messageType, text and type properties
     */
    handleStructuredContentJSON(renderer, message, structuredContent) {
        // console.log(structuredContent);
        if (structuredContent.type === 'Message') {
            // message consists only of text
            if (!structuredContent.content &&
                !structuredContent.contentType &&
                !structuredContent.messageType &&
                structuredContent.text) {
                renderer.addText(structuredContent);
            }
            // message has text and quickreplies
            if (structuredContent.content &&
                structuredContent.contentType === 'quick-replies' &&
                structuredContent.messageType === 'text') {
                renderer.addText(structuredContent);
                // TODO Den ganzen block quickreply prepend und append in den Element renderer
                renderer.quickReplyPrepend();
                for (let contentEntry of structuredContent.content) {
                    renderer.addQuickReply(contentEntry, message.index);
                }
                renderer.quickReplyAppend();
            }
        }
        if (structuredContent.carousel) {
            renderer.addCarousel(structuredContent, message.index);
        }
        if (!structuredContent.carousel && structuredContent.type === 'Structured') {
            let imageButton = false;
            // parse structuredContent.content which holds components
            for (let contentEntry of structuredContent.content) {
                // console.log(contentEntry);
                if (contentEntry.image) {
                    renderer.addImage(contentEntry);
                    imageButton = true;
                }
                if (contentEntry.video) {
                    renderer.addVideo(contentEntry);
                    imageButton = true;
                }
                if (contentEntry['components']) {
                    imageButton ? renderer.buttonPrependImage() : renderer.buttonPrepend();
                    for (let component of contentEntry['components']) {
                        if (component.type && component.type === 'button') {
                            renderer.addButton(component);
                        }
                    }
                    renderer.buttonAppend();
                    imageButton = false;
                }
            }
        }
        if (structuredContent.type === DebugInfoEvent.getType()) {
            renderer.addDebugInfo(structuredContent);
        }
        if (structuredContent.type === InvoiceEvent.getType()) {
            renderer.addInvoiceElement(structuredContent);
        }
    }
}
